import { PipelineColumn } from '@/types';

export type PipelineTemplateId =
    | 'default'
    | 'research'
    | 'mergers-acquisitions'
    | 'due-diligence'
    | 'compliance'
    | 'fundraising';

export interface PipelineTemplateDefinition {
    columns: Partial<PipelineColumn>[];
    description: string;
    id: PipelineTemplateId;
    name: string;
}

interface PipelineTemplatePalette {
    blue: string;
    cyan: string;
    gold: string;
    green: string;
    orange: string;
    purple: string;
    red: string;
    text: string;
}

const stages = (palette: PipelineTemplatePalette, definitions: [string, keyof PipelineTemplatePalette][]) =>
    definitions.map(([name, color], index) => ({ color: palette[color], index, name }));

export const createPipelineTemplates = (palette: PipelineTemplatePalette): PipelineTemplateDefinition[] => [
    {
        id: 'default',
        name: 'Default Pipeline',
        description: 'A flexible workflow for general research and allocation decisions.',
        columns: stages(palette, [
            ['Discovery', 'gold'],
            ['In Progress', 'cyan'],
            ['In Review', 'blue'],
            ['Awaiting Feedback', 'purple'],
            ['Allocated', 'green'],
            ['Cancelled', 'red'],
        ]),
    },
    {
        id: 'research',
        name: 'Research Pipeline',
        description: 'Move ideas from sourcing through peer review and a decision-ready thesis.',
        columns: stages(palette, [
            ['Research Backlog', 'text'],
            ['Sourcing', 'gold'],
            ['Analysis', 'cyan'],
            ['Peer Review', 'purple'],
            ['Thesis Ready', 'blue'],
            ['Published', 'green'],
        ]),
    },
    {
        id: 'mergers-acquisitions',
        name: 'M&A Pipeline',
        description: 'Track opportunities from origination through diligence, negotiation, and close.',
        columns: stages(palette, [
            ['Origination', 'gold'],
            ['Initial Review', 'cyan'],
            ['NDA & Data Room', 'blue'],
            ['Due Diligence', 'purple'],
            ['Investment Committee', 'orange'],
            ['Negotiation', 'green'],
            ['Closed', 'green'],
            ['Passed', 'red'],
        ]),
    },
    {
        id: 'due-diligence',
        name: 'Due Diligence',
        description: 'Coordinate evidence requests, reviews, follow-ups, approvals, and exceptions.',
        columns: stages(palette, [
            ['Requested', 'text'],
            ['Received', 'cyan'],
            ['In Review', 'blue'],
            ['Follow-up Required', 'orange'],
            ['Approved', 'green'],
            ['Exception', 'red'],
        ]),
    },
    {
        id: 'compliance',
        name: 'Compliance Review',
        description: 'Manage testing, evidence, remediation, and final compliance review.',
        columns: stages(palette, [
            ['Intake', 'text'],
            ['Evidence Requested', 'gold'],
            ['Testing', 'cyan'],
            ['Remediation', 'orange'],
            ['Final Review', 'purple'],
            ['Closed', 'green'],
        ]),
    },
    {
        id: 'fundraising',
        name: 'Fundraising Pipeline',
        description: 'Organize investor outreach from prospecting through commitment.',
        columns: stages(palette, [
            ['Prospect', 'text'],
            ['Contacted', 'gold'],
            ['Meeting', 'cyan'],
            ['Investor Diligence', 'blue'],
            ['Terms', 'purple'],
            ['Committed', 'green'],
            ['Passed', 'red'],
        ]),
    },
];
